// components/ModalFormRemitoc.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { putRemitoc } from "../helpers/rutaRemitocs";
import { getProveedores } from "../helpers/rutaProveedores";
import { getClientes } from "../helpers/rutaClientes";
import { getTransportes } from "../helpers/rutaTransportes";
import { getChoferes } from "../helpers/rutaChoferes";
import { getLotes } from "../helpers/rutaLotes";
import { getEstados } from "../helpers/rutaEstados";
import { getObservas } from "../helpers/rutaObservas";

const sortBy = (arr, key) =>
  [...(arr || [])].sort((a, b) =>
    (a?.[key] || "").localeCompare(b?.[key] || "", "es", { sensitivity: "base" })
  );

const ModalFormRemitoc = ({ show, remitoc, onClose }) => {
  const close = () => {
    try {
      onClose?.();
    } catch (e) {
      console.error(e);
    }
  };

  const [form, setForm] = useState({
    // base
    fecha: "",
    horaCarga: "",
    kgCampo: "",
    kgRecibido: "",
    precioCompra: "",
    precioVenta: "",
    proveedor: "",
    cliente: "",
    clientereal: "",
    transporte: "",
    chofer: "",
    lote: "",
    variedad: "",
    nroDTVCompra: "",
    nroDTVVenta: "",
    nota: "",
    // extra RemitoC
    remitoint: "",
    estado: "",
    observa: "",
    factcompra: "",
    factventa: "",
    tilde: false,
    color: false,
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Cargar valores iniciales desde remitoc
  useEffect(() => {
    if (!remitoc) return;

    const proveedorId =
      typeof remitoc.proveedor === "object"
        ? remitoc.proveedor?._id
        : remitoc.proveedor || "";

    const clienteId =
      typeof remitoc.cliente === "object"
        ? remitoc.cliente?._id
        : remitoc.cliente || "";

    let clienterealId =
      typeof remitoc.clientereal === "object"
        ? remitoc.clientereal?._id
        : remitoc.clientereal || "";

    // Si no hay clientereal grabado, lo igualamos al cliente
    if (!clienterealId && clienteId) {
      clienterealId = clienteId;
    }

    const transporteId =
      typeof remitoc.transporte === "object"
        ? remitoc.transporte?._id
        : remitoc.transporte || "";

    const choferId =
      typeof remitoc.chofer === "object"
        ? remitoc.chofer?._id
        : remitoc.chofer || "";

    const loteId =
      typeof remitoc.lote === "object" ? remitoc.lote?._id : remitoc.lote || "";

    const estadoId =
      typeof remitoc.estado === "object"
        ? remitoc.estado?._id
        : remitoc.estado || "";

    const observaId =
      typeof remitoc.observa === "object"
        ? remitoc.observa?._id
        : remitoc.observa || "";

    setForm({
      fecha: remitoc.fecha
        ? new Date(remitoc.fecha).toISOString().slice(0, 10)
        : "",
      horaCarga: remitoc.horaCarga || "",      // 👈 NUEVO
      kgCampo: remitoc.kgCampo ?? "",
      kgRecibido: remitoc.kgRecibido ?? "",
      precioCompra: remitoc.precioCompra ?? "",
      precioVenta: remitoc.precioVenta ?? "",
      proveedor: proveedorId,
      cliente: clienteId,
      clientereal: clienterealId,
      transporte: transporteId,
      chofer: choferId,
      lote: loteId,
      variedad: remitoc.variedad || "",
      nroDTVCompra: remitoc.nroDTVCompra || "",
      nroDTVVenta: remitoc.nroDTVVenta || "",
      nota: remitoc.nota || "",
      remitoint: remitoc.remitoint || "",
      estado: estadoId,
      observa: observaId,
      factcompra: remitoc.factcompra || "",
      factventa: remitoc.factventa || "",
      tilde: !!remitoc.tilde,
      color: !!remitoc.color,
    });
  }, [remitoc]);

  // handle: sincroniza cliente -> clientereal
  const handle = (e) => {
    const { name, type, checked, value } = e.target;

    setForm((s) => {
      const next = {
        ...s,
        [name]: type === "checkbox" ? !!checked : value,
      };

      // si cambia cliente, igualamos clientereal
      if (name === "cliente") {
        next.clientereal = value || "";
      }

      return next;
    });
  };

  // derivados (solo visual)
  const kgRecibidoNum = Number(form.kgRecibido || 0);
  const kgCampoNum = Number(form.kgCampo || 0);
  const diferencia = kgCampoNum - kgRecibidoNum;
  const totalCompra = kgRecibidoNum * Number(form.precioCompra || 0);
  const totalVenta = kgRecibidoNum * Number(form.precioVenta || 0);
  const comision = totalVenta - totalCompra;

  // listas
  const [lists, setLists] = useState({
    prov: [],
    cli: [],
    tra: [],
    chof: [],
    lot: [],
    est: [],
    obs: [],
    loading: true,
    error: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [prov, cli, tra, chof, lot, est, obs] = await Promise.all([
          getProveedores(),
          getClientes(),
          getTransportes(),
          getChoferes(),
          getLotes(),
          getEstados(),
          getObservas(),
        ]);
        setLists({
          prov: Array.isArray(prov) ? prov : prov?.proveedores || [],
          cli: Array.isArray(cli) ? cli : cli?.clientes || [],
          tra: Array.isArray(tra) ? tra : tra?.transportes || [],
          chof: Array.isArray(chof) ? chof : chof?.choferes || [],
          lot: Array.isArray(lot) ? lot : lot?.lotes || [],
          est: Array.isArray(est) ? est : est?.estados || [],
          obs: Array.isArray(obs) ? obs : obs?.observas || [],
          loading: false,
          error: "",
        });
      } catch (e) {
        setLists({
          prov: [],
          cli: [],
          tra: [],
          chof: [],
          lot: [],
          est: [],
          obs: [],
          loading: false,
          error: e?.message || "Error al cargar listas",
        });
      }
    })();
  }, []);

  const provOpts = useMemo(
    () => sortBy(lists.prov, "razonsocial"),
    [lists.prov]
  );
  const cliOpts = useMemo(
    () => sortBy(lists.cli, "razonsocial"),
    [lists.cli]
  );
  const traOpts = useMemo(
    () => sortBy(lists.tra, "transporte"),
    [lists.tra]
  );
  const chofOpts = useMemo(
    () => sortBy(lists.chof, "nombre"),
    [lists.chof]
  );
  const lotOpts = useMemo(() => sortBy(lists.lot, "lote"), [lists.lot]);
  const estOpts = useMemo(
    () => sortBy(lists.est, "estado"),
    [lists.est]
  );
  const obsOpts = useMemo(
    () => sortBy(lists.obs, "observa"),
    [lists.obs]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!remitoc?._id) return;

    const clean = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [
        k,
        typeof v === "string" ? v.trim() : v,
      ])
    );

    try {
      setSaving(true);
      const resp = await putRemitoc(remitoc._id, clean);
      if (resp?.ok === false) throw { response: { data: resp } };

      setSaved(true);
      setSaving(false);
      setTimeout(() => {
        setSaved(false);
        close();
      }, 1000);
    } catch (error) {
      setSaving(false);
      const d = error?.response?.data;
      const msg =
        d?.err?.message ||
        d?.error?.message ||
        d?.message ||
        "No se pudo actualizar el Remito C.";
      alert(msg);
    }
  };

  return (
    <Modal show={show} onHide={close} size="lg" animation={false}>
      <form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {remitoc ? `Modificar Remito C #${remitoc.codrem}` : "Cargando…"}
          </Modal.Title>
        </Modal.Header>

        {saved && (
          <div
            className="alert alert-success m-0 rounded-0"
            role="status"
            aria-live="polite"
          >
            ✅ ¡Cambios guardados!
          </div>
        )}

        <Modal.Body>
          {!remitoc ? (
            <div className="p-3">Cargando datos…</div>
          ) : (
            <fieldset disabled={saving}>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label>Fecha</label>
                  <input
                    type="date"
                    name="fecha"
                    className="form-control"
                    value={form.fecha}
                    onChange={handle}
                    required
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label>Hora de carga</label>
                  <input
                    type="time"
                    name="horaCarga"
                    className="form-control"
                    value={form.horaCarga}
                    onChange={handle}
                    required
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label>KG Campo</label>
                  <input
                    type="number"
                    name="kgCampo"
                    className="form-control"
                    value={form.kgCampo}
                    onChange={handle}
                    min="0"
                    step="1"
                    required
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label>KG Recibidos</label>
                  <input
                    type="number"
                    name="kgRecibido"
                    className="form-control"
                    value={form.kgRecibido}
                    onChange={handle}
                    min="0"
                    step="1"
                    required
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label>Diferencia</label>
                  <input className="form-control" value={diferencia} readOnly />
                </div>

                {/* 
                <div className="col-md-3 mb-3">
                  <label>Precio Compra ($/kg)</label>
                  <input
                    type="number"
                    name="precioCompra"
                    className="form-control"
                    value={form.precioCompra}
                    onChange={handle}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label>Precio Venta ($/kg)</label>
                  <input
                    type="number"
                    name="precioVenta"
                    className="form-control"
                    value={form.precioVenta}
                    onChange={handle}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div className="col-md-2 mb-3">
                  <label>Total Compra</label>
                  <input
                    className="form-control"
                    value={totalCompra.toLocaleString("es-AR", {
                      minimumFractionDigits: 2,
                    })}
                    readOnly
                  />
                </div>
                <div className="col-md-2 mb-3">
                  <label>Total Venta</label>
                  <input
                    className="form-control"
                    value={totalVenta.toLocaleString("es-AR", {
                      minimumFractionDigits: 2,
                    })}
                    readOnly
                  />
                </div>
                <div className="col-md-2 mb-3">
                  <label>Comisión</label>
                  <input
                    className="form-control"
                    value={comision.toLocaleString("es-AR", {
                      minimumFractionDigits: 2,
                    })}
                    readOnly
                  />
                </div>
                */}

                <div className="col-md-4 mb-3">
                  <label>Proveedor</label>
                  <select
                    className="form-control"
                    name="proveedor"
                    value={form.proveedor}
                    onChange={handle}
                    required
                  >
                    <option value="">Elija opción</option>
                    {provOpts.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.razonsocial}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label>Cliente</label>
                  <select
                    className="form-control"
                    name="cliente"
                    value={form.cliente}
                    onChange={handle}
                    required
                  >
                    <option value="">Elija opción</option>
                    {cliOpts.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.razonsocial}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label>Cliente Real</label>
                  <select
                    className="form-control"
                    name="clientereal"
                    value={form.clientereal}
                    disabled
                  >
                    <option value="">
                      {form.cliente
                        ? cliOpts.find((c) => c._id === form.cliente)
                            ?.razonsocial || "Igual a cliente"
                        : "Seleccione primero un cliente"}
                    </option>
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label>Transporte</label>
                  <select
                    className="form-control"
                    name="transporte"
                    value={form.transporte}
                    onChange={handle}
                    required
                  >
                    <option value="">Elija opción</option>
                    {traOpts.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.transporte}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label>Chofer</label>
                  <select
                    className="form-control"
                    name="chofer"
                    value={form.chofer}
                    onChange={handle}
                  >
                    <option value="">Elija opción</option>
                    {chofOpts.map((ch) => (
                      <option key={ch._id} value={ch._id}>
                        {ch.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label>Lote</label>
                  <select
                    className="form-control"
                    name="lote"
                    value={form.lote}
                    onChange={handle}
                  >
                    <option value="">Elija opción</option>
                    {lotOpts.map((l) => (
                      <option key={l._id} value={l._id}>
                        {l.lote}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label>Variedad</label>
                  <input
                    className="form-control"
                    name="variedad"
                    value={form.variedad}
                    onChange={handle}
                  />
                </div>

                {/* Extra Remito C */}
                <div className="col-md-4 mb-3">
                  <label>Remito Interno</label>
                  <input
                    className="form-control"
                    name="remitoint"
                    value={form.remitoint}
                    onChange={handle}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label>Estado</label>
                  <select
                    className="form-control"
                    name="estado"
                    value={form.estado}
                    onChange={handle}
                  >
                    <option value="">Elija opción</option>
                    {estOpts.map((e) => (
                      <option key={e._id} value={e._id}>
                        {e.estado}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label>Observación</label>
                  <select
                    className="form-control"
                    name="observa"
                    value={form.observa}
                    onChange={handle}
                  >
                    <option value="">Elija opción</option>
                    {obsOpts.map((o) => (
                      <option key={o._id} value={o._id}>
                        {o.observa}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4 mb-3">
                  <label>Factura Compra</label>
                  <input
                    className="form-control"
                    name="factcompra"
                    value={form.factcompra}
                    onChange={handle}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label>Factura Venta</label>
                  <input
                    className="form-control"
                    name="factventa"
                    value={form.factventa}
                    onChange={handle}
                  />
                </div>

                <div className="col-md-2 mb-3 d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    id="tilde"
                    name="tilde"
                    checked={!!form.tilde}
                    onChange={handle}
                  />
                  <label htmlFor="tilde" className="mb-0">
                    Tilde
                  </label>
                </div>
                <div className="col-md-2 mb-3 d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    id="color"
                    name="color"
                    checked={!!form.color}
                    onChange={handle}
                  />
                  <label htmlFor="color" className="mb-0">
                    Color
                  </label>
                </div>

                <div className="col-md-4 mb-3">
                  <label>Nro DTV Compra</label>
                  <input
                    className="form-control"
                    name="nroDTVCompra"
                    value={form.nroDTVCompra}
                    onChange={handle}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label>Nro DTV Venta</label>
                  <input
                    className="form-control"
                    name="nroDTVVenta"
                    value={form.nroDTVVenta}
                    onChange={handle}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>Notas</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="nota"
                    value={form.nota}
                    onChange={handle}
                  />
                </div>
              </div>
            </fieldset>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={close} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" variant="dark" disabled={!remitoc || saving}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default ModalFormRemitoc;
